import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Header, Content, HeaderTop, NewBatchButtonWrapper, NewBatchButton, DropdownMenu, DropdownItem, WrapperIcon, DropdownItemContent, DropdownItemTitle, DropdownItemSubtitle, Divider, ApprovalButton, MoreButton, ChevronIcon, ArchivedFilterButton } from './Styles';
import { StatusFilters } from './Styles';
import { Col, DatePicker, Icon, Input, message, Row, Select, Tooltip } from 'antd';
import client, { setStatusCodeCallback } from '../../../utils/Apollo/ApolloClient';
import { ApolloProvider } from '@apollo/client';
import moment from 'moment';
import StatusMonitoringContent from './StatusMonitoringContent';
import { getAllTeams } from '../../../utils/services/teamsAPI';
import { useSessionContext, checkUserAccess } from '../../../context/sessionContext';
import { STATUS_MONITORING, STATUS_MONITORING as trans, BATCH_PROCESS } from './TranslationValues';
import { withTranslation } from 'react-i18next';
import { batchStatus, batchInitiation } from '../../../utils/constants/statusMonitoring';
import { getUser } from '../../../utils/services/userAPI';
import { Title } from '../newDashboardStyles';
import { ArchiveSVG, ChevronDownIconSVG, FileStackSVG } from '../../../images/svgIcons';
import FontsProvider from '../../../context/fontsContext';
import SendLettersModal from './HybridMail/SendLettersModal';
import HybridMail from './HybridMail';
import CreateBatch from './BatchJobs/CreateBatch';
import { checkUserAccessRoles } from '../../../utils/helper';
import { HybridMailProvider } from './HybridMail/HybridMailContext';

const NAMESPACES = ['STATUS_MONITORING', 'BATCH_PROCESS', 'HYBRID_MAIL'];

const { RangePicker } = DatePicker;
const { Option } = Select;

const SIDEBAR_PAGINATION_HEIGHT = 52;
const BATCH_CARD_HEIGHT = 110;

const StatusMonitoring = ({ t }) => {
  const startDate = moment()
    .subtract(7, 'day')
    .startOf('day');
  const endDate = moment().endOf('day');
  const formattedStartDate = startDate.format('YYYY-MM-DDTHH:mm:ss');
  const formattedEndDate = endDate.format('YYYY-MM-DDTHH:mm:ss');
  const [filters, setFilters] = useState({
    filter: { page: 1, startDate: formattedStartDate, endDate: formattedEndDate },
  });
  const [cardCount, setCardCount] = useState(5);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [showBatchMenu, setShowBatchMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [hybridModal, setHybridModal] = useState(false);
  const [hybridInfo, setHybridInfo] = useState(null);
  const [draftBatchInfo, setDraftBatchInfo] = useState(undefined);
  const sidebarRef = useRef(null);

  const [requiresMyApproval, setRequiresMyApproval] = useState(false);
  const [showArchivedBatches, setShowArchivedBatches] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const isJobManager = checkUserAccessRoles(userDetails, ['JOBMANAGER']);

  const hasHybridRole = checkUserAccessRoles(userDetails, ['HYBRID']);

  const fetchData = useCallback(async () => {
    try {
      const teams = await getAllTeams();
      if(isJobManager) {
        const users = await getUser();
        setUsers(users?.data);
        setTeams(teams?.data);
      } else {
        const userTeamIds = userDetails.resourceGroupPermission.write;
        const userTeams = teams?.data?.filter(team => userTeamIds.includes(team.id));
        setTeams(userTeams);
      }
    } catch (e) {
      console.log(e.error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const sidebarHeight = sidebarRef.current?.clientHeight;
    if (sidebarHeight) {
      const count = Math.floor((sidebarHeight - SIDEBAR_PAGINATION_HEIGHT) / BATCH_CARD_HEIGHT);
      setCardCount(count === 0 ? 5 : count);
      setFilters(prevFilter => ({
        filter: {
          ...prevFilter.filter,
          limit: count === 0 ? 5 : count,
        },
      }));
    }
  }, []);

  const onDateTimeRangeChange = dates => {
    const startDate = dates[0].format('YYYY-MM-DDTHH:mm:ss'); // Customize the format as needed
    const endDate = dates[1].format('YYYY-MM-DDTHH:mm:ss');
    setFilters(prevFilter => ({
      filter: {
        ...prevFilter.filter,
        page: 1,
        startDate,
        endDate,
      },
    }));
  };

  const handleSelectChange = (value, key) => {
    if(key === 'teamId' || key === 'triggeredBy' || key === 'status' || key === 'batchType') {
      setFilterValues(prevFilterValues => {
        const prevFilters = {
          ...prevFilterValues,
          [key]: value === '' ? null : value,
        };
        if (key === 'batchType' && value === 'HYBRID' && 'teamId' in prevFilters) {
          prevFilters.teamId = null;
        }
        return prevFilters;
      });
    }
    
    setFilters(prevFilter => {
      const payload = {
        ...prevFilter.filter,
        page: 1,
        [key]: key === 'myApproval'? Boolean(value) : value === '' || (key === 'status' && value.length === 0) ? null : value,
      };
      if (key === 'batchType' && value === 'HYBRID') {
        if ('teamId' in payload) {
          payload.teamId = null;
        }
        if ('initiatedFrom' in payload) {
          payload.initiatedFrom = null;
        }
      }
      if (key === 'initiatedFrom') {
        if (value === 'API') {
          if (filters.filter?.teamId) {
            payload.teamId = null;
          }
          if (filters.filter?.triggeredBy) {
            payload.triggeredBy = null;
          }
        } else {
          if (filterValues?.teamId) {
            payload.teamId = filterValues.teamId;
          }
          if (filterValues?.triggeredBy) {
            payload.triggeredBy = filterValues.triggeredBy;
          }
        }
      }
      return { filter: payload };
    });
  };

  const handleHybridSubmit = (info) => {
    setHybridInfo(info);
    setHybridModal(false);
    setCurrentPage('hybridMail');
  }

  const batchTypes = [
    { label: t(trans.ALL_LABEL.key, trans.ALL_LABEL.value), value: '' },
    { label: t(trans.STANDARD_LABEL.key, trans.STANDARD_LABEL.value), value: 'STANDARD' },
    { label: t(trans.ADVANCED_LABEL.key, trans.ADVANCED_LABEL.value), value: 'ADVANCED' },
    { label: t(trans.HYBRID_LABEL.key, trans.HYBRID_LABEL.value), value: 'HYBRID' },
    { label: t(trans.API_LABEL.key, trans.API_LABEL.value), value: 'API' },
  ];

  const handleMyApproval = () => {
    setRequiresMyApproval(prev => !prev);
    handleSelectChange(!requiresMyApproval, 'myApproval');
  }

  const handleArchivedBatches = () => {
    const nextValue = !showArchivedBatches;
    setShowArchivedBatches(nextValue);
    setFilters(prevFilter => ({
      filter: {
        ...prevFilter.filter,
        page: 1,
        isArchived: nextValue,
      },
    }));
  };

  return (
    <Container>
      { currentPage === 'dashboard' && 
        <>
          <Header>
            <HeaderTop>
              <Title>{t(trans.STATUS_MONITORING_TITLE.key, trans.STATUS_MONITORING_TITLE.value)}</Title>

              <NewBatchButtonWrapper>
                <NewBatchButton open={showBatchMenu} onClick={() => setShowBatchMenu(!showBatchMenu)}>
                  <Icon className="plus-icon" type="plus" />
                  <span className="button-text">
                    {t(BATCH_PROCESS.NEW_BATCH.key, BATCH_PROCESS.NEW_BATCH.value)}
                  </span>
                  <ChevronDownIconSVG />
                </NewBatchButton>

                {showBatchMenu && (
                  <DropdownMenu>
                    <DropdownItem
                      variant="hybrid"
                      onClick={() => {
                        if(hasHybridRole) {
                          setShowBatchMenu(false);
                          setHybridModal(true);
                        } else {
                          message.error(t(trans.HYBRID_ROLE_REQUIRED_ERROR.key, trans.HYBRID_ROLE_REQUIRED_ERROR.value));
                        }
                      }}
                      className={!hasHybridRole ? 'disabled' : ''}
                    >
                      <WrapperIcon
                        bgColor="rgba(146, 123, 248, 0.2)"
                        hoverBg="rgba(146, 123, 248, 0.3)"
                      >
                        <Icon type="mail" style={{ color: '#927BF8', fontSize: '16px' }} />
                      </WrapperIcon>
                      <DropdownItemContent>
                        <DropdownItemTitle>
                          {t(
                            BATCH_PROCESS.HYBRID_MAIL_TITLE.key,
                            BATCH_PROCESS.HYBRID_MAIL_TITLE.value,
                          )}
                        </DropdownItemTitle>
                        <DropdownItemSubtitle>
                          {t(
                            BATCH_PROCESS.HYBRID_MAIL_SUB_TITLE.key,
                            BATCH_PROCESS.HYBRID_MAIL_SUB_TITLE.value,
                          )}
                        </DropdownItemSubtitle>
                      </DropdownItemContent>
                    </DropdownItem>

                    <Divider />

                    <DropdownItem
                      onClick={() => {
                        setShowBatchMenu(false);
                        setCurrentPage('createBatch');
                      }}
                    >
                      <WrapperIcon bgColor="#cefafe" hoverBg="#a2f4fd">
                        <FileStackSVG />
                      </WrapperIcon>
                      <DropdownItemContent>
                        <DropdownItemTitle>
                          {t(BATCH_PROCESS.BATCH_JOB_TITLE.key, BATCH_PROCESS.BATCH_JOB_TITLE.value)}
                        </DropdownItemTitle>
                        <DropdownItemSubtitle>
                          {t(
                            BATCH_PROCESS.BATCH_JOB_SUB_TITLE.key,
                            BATCH_PROCESS.BATCH_JOB_SUB_TITLE.value,
                          )}
                        </DropdownItemSubtitle>
                      </DropdownItemContent>
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </NewBatchButtonWrapper>
            </HeaderTop>
            <StatusFilters statusMonitoring>
              <Row
                type="flex"
                gutter={16}
                style={{ width: '100%', rowGap: '10px', justifyContent: 'flex-start', alignItems: 'flex-end' }}
              >
                <Col md={12} lg={6}>
                  <p className="label">
                    {t(trans.DATE_FILTER_LABEL.key, trans.DATE_FILTER_LABEL.value)}
                  </p>
                  <RangePicker
                    // disabledDate={disabledDate}
                    inputReadOnly={true}
                    allowClear={false}
                    showTime={{
                      hideDisabledOptions: true,
                      defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')],
                    }}
                    defaultValue={[startDate, endDate]}
                    format="YYYY-MM-DD HH:mm:ss"
                    onOk={onDateTimeRangeChange}
                    disabledDate={current => current > moment().endOf('day')}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col md={12} lg={3}>
                  <p className="label">{t(trans.JOB_TYPE_LABEL.key, trans.JOB_TYPE_LABEL.value)}</p>
                  <Select
                    style={{ width: '95%' }}
                    value={filterValues?.batchType || ''}
                    placeholder={t(trans.JOB_TYPE_LABEL.key, trans.JOB_TYPE_LABEL.value)}
                    onChange={val => handleSelectChange(val, 'batchType')}
                    showArrow
                    filterOption={(input, option) => {
                      if (typeof option.props.children === 'string') {
                        return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      } else {
                        return (
                          option.props.children.props.title
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        );
                      }
                    }}
                  >
                    {batchTypes.map(({ label, value }) => (
                      <Option key={value} value={value}>
                        <Tooltip key={value} placement="topLeft" title={label}>
                          {label}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col md={12} lg={4}>
                  <p className="label">
                    {t(trans.STATUS_FILTER_LABEL.key, trans.STATUS_FILTER_LABEL.value)}
                  </p>
                  <Select
                    mode='multiple'
                    style={{ width: '95%' }}
                    value={filterValues?.status || []}
                    placeholder={t(trans.STATUS_FILTER_LABEL.key, trans.STATUS_FILTER_LABEL.value)}
                    onChange={val => handleSelectChange(val, 'status')}
                    showArrow
                    maxTagCount={1}
                    maxTagPlaceholder={`+${filterValues.status?.length-1}`}
                    allowClear
                    filterOption={(input, option) => {
                      if (typeof option.props.children === 'string') {
                        return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      } else {
                        return (
                          option.props.children.props.title
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        );
                      }
                    }}
                  >
                    {batchStatus(t, userDetails?.roles, filterValues.batchType).map(status => (
                      <Option key={status.id} value={status.value}>
                        <Tooltip key={status.id} placement="topLeft" title={status.key}>
                          {status.key}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col md={12} lg={6}>
                  <p className="label">
                    {t(trans.JOB_NAME_FILTER_LABEL.key, trans.JOB_NAME_FILTER_LABEL.value)}
                  </p>
                  <Input
                    placeholder={t(
                      trans.JOB_NAME_FILTER_LABEL.key,
                      trans.JOB_NAME_FILTER_LABEL.value,
                    )}
                    onChange={e => handleSelectChange(e.target.value, 'batchName')}
                  />
                </Col>
                <Col md={12} lg={5} style={{ display: 'flex', gap: '6px' }}>
                  <ApprovalButton
                    active={requiresMyApproval}
                    onClick={handleMyApproval}
                  >
                    <div active={requiresMyApproval}>
                      {requiresMyApproval && <Icon type="check" />}
                    </div>
                    <span>{t(trans.MY_APPROVAL.key, trans.MY_APPROVAL.value)}</span>
                  </ApprovalButton>
                  
                  <MoreButton onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                    <Icon type="filter" size={14} />
                    <span>{t(trans.MORE_LABEL.key, trans.MORE_LABEL.value)}</span>
                    <ChevronIcon open={showAdvancedFilters}>
                      <Icon type="down" size={12} />
                    </ChevronIcon>
                  </MoreButton>
                </Col>
              </Row>
              {showAdvancedFilters &&
                <Row type="flex"
                  gutter={16}
                  style={{ width: '100%', rowGap: '10px', justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                    {teams?.length > 1 && <Col md={12} lg={6}>
                      <p className="label">
                        {t(trans.TEAM_FILTER_LABEL.key, trans.TEAM_FILTER_LABEL.value)}
                      </p>
                      <Select
                        showSearch
                        style={{ width: '95%' }}
                        optionFilterProp="children"
                        defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        value={ filterValues.teamId ? filterValues.teamId : t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        onChange={val => handleSelectChange(val, 'teamId')}
                        filterOption={(input, option) => {
                          if (typeof option.props.children === 'string') {
                            return (
                              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            );
                          } else {
                            return (
                              option.props.children.props.title
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            );
                          }
                        }}
                        disabled={filters.filter['initiatedFrom'] === 'API' || filterValues.batchType === 'HYBRID'}
                      >
                        <Option value="" selected>
                          {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        </Option>
                        {teams.map(team => (
                          <Option key={team.id} value={team.id}>
                            <Tooltip key={team.id} placement="topLeft" title={team.name}>
                              {team.name}
                            </Tooltip>
                          </Option>
                        ))}
                      </Select>
                    </Col>}
                {isJobManager && (
                  <>
                    <Col md={12} lg={6}>
                      <p className="label">
                        {t(trans.INITIATION_FILTER_LABEL.key, trans.INITIATION_FILTER_LABEL.value)}
                      </p>
                      <Select
                        showSearch
                        style={{ width: '95%' }}
                        optionFilterProp="children"
                        defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        value={filters.filter?.initiatedFrom ?? t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        onChange={val => handleSelectChange(val, 'initiatedFrom')}
                        filterOption={(input, option) => {
                          if (typeof option.props.children === 'string') {
                            return (
                              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            );
                          } else {
                            return (
                              option.props.children.props.title
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            );
                          }
                        }}
                        disabled={filterValues?.batchType === 'HYBRID'}
                      >
                        <Option value="" selected>
                          {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        </Option>
                        {batchInitiation(t).map(status => (
                          <Option key={status.id} value={status.key}>
                            <Tooltip key={status.id} placement="topLeft" title={status.value}>
                              {status.value}
                            </Tooltip>
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col md={12} lg={6}>
                      <p className="label">
                        {t(trans.INITIATED_BY_LABEL.key, trans.INITIATED_BY_LABEL.value)}
                      </p>
                      <Select
                        showSearch
                        style={{ width: '95%' }}
                        optionFilterProp="children"
                        defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        value={filterValues.triggeredBy ?? t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        onChange={val => handleSelectChange(val, 'triggeredBy')}
                        filterOption={(input, option) => {
                          if (typeof option.props.children === 'string') {
                            return (
                              option.props.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            );
                          } else {
                            return (
                              option.props.children.props.title
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            );
                          }
                        }}
                        disabled={filters.filter['initiatedFrom'] === 'API'}
                      >
                        <Option value="" selected>
                          {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                        </Option>
                        {users.map(user => (                          
                          (user.displayName?.trim() && <Option key={user.id} value={user.id}>
                            <Tooltip key={user.id} placement="topLeft" title={user.displayName}>
                              {user.displayName}
                            </Tooltip>
                          </Option>
                        )))}
                      </Select>
                    </Col>
                    {isJobManager && <Col md={12} lg={2} className="archived-filter-column">
                      <ArchivedFilterButton type="button" active={showArchivedBatches} onClick={handleArchivedBatches}>
                      { showArchivedBatches ? <Icon type='check-circle'/> : <ArchiveSVG size={14} color={'#000'} />}
                        <span>{t(trans.ARCHIVED_FILTER.key, trans.ARCHIVED_FILTER.value)}</span>
                      </ArchivedFilterButton>
                    </Col>}
                    <Col md={12} lg={2}>
                    <span className='hide_button' onClick={() => setShowAdvancedFilters(false)}>{t(trans.HIDE_LABEL.key, trans.HIDE_LABEL.value)}</span>
                    </Col>
                  </>
                )}
              </Row>}
            </StatusFilters>
          </Header>
          <Content ref={sidebarRef} showSidebar={showSidebar}>
            <StatusMonitoringContent
              setHybridInfo={setHybridInfo}
              setCurrentPage={setCurrentPage}
              filters={filters}
              setFilters={setFilters}
              cardCount={cardCount}
              users={users}
              showSidebar={showSidebar}
              setShowSidebar={setShowSidebar}
              setDraftBatchInfo={setDraftBatchInfo}
              isJobManager={isJobManager}
            />
          </Content>
          <FontsProvider>
            <HybridMailProvider>
            <SendLettersModal visible={hybridModal} setHybridModal={setHybridModal} handleOnSubmit={handleHybridSubmit} setHybridInfo={setHybridInfo} setCurrentPage={setCurrentPage} />
            </HybridMailProvider>
            </FontsProvider>
        </>
      }
      {currentPage === 'hybridMail' && (hybridInfo) && (
        <FontsProvider>
          <HybridMail
            visible={true}
            hybridInfo={hybridInfo}
            onRefresh
            setCurrentPage={setCurrentPage}
            setFilterValues={setFilterValues}
            setFilters={setFilters}
            setRequiresMyApproval={setRequiresMyApproval}
            t={t}
          />
        </FontsProvider>
      )}
      {
        currentPage === 'createBatch' &&
         <CreateBatch
          setCurrentPage={setCurrentPage}
          setFilterValues={setFilterValues}
          setFilters={setFilters}
          setRequiresMyApproval={setRequiresMyApproval}
          draftBatchInfo={draftBatchInfo} 
          setDraftBatchInfo={setDraftBatchInfo}
          />
      }
    </Container>
  );
};

const StatusLiveMonitoring = ({ t }) => {
  useEffect(() => {
    const handleStatusCode = code => {
      code !== 4002 &&
        message.error(
          t(STATUS_MONITORING.GET_TOKEN_FAILED.key, STATUS_MONITORING.GET_TOKEN_FAILED.value),
        );
    };

    setStatusCodeCallback(handleStatusCode);

    return () => {
      setStatusCodeCallback(null);
    };
  }, []);

  return (
    <ApolloProvider client={client}>
      <StatusMonitoring t={t} />
    </ApolloProvider>
  );
};

export default withTranslation(NAMESPACES)(StatusLiveMonitoring);
